import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { api } from '@/lib/api';

export interface ApiTokenDTO {
  id: number;
  user_id: number;
  nome: string;
  escopo: string;
  ultimo_uso_em: string | null;
  ultimo_ip: string | null;
  criado_em: string;
  revogado_em: string | null;
}

interface ListResponse {
  tokens: ApiTokenDTO[];
  total: number;
}

export function useTokens(includeRevoked = false): UseQueryResult<ListResponse> {
  const qs = includeRevoked ? '?all=true' : '';
  return useQuery({
    queryKey: ['tokens', includeRevoked],
    queryFn: () => api<ListResponse>(`/api/tokens${qs}`),
  });
}

export function useCreateToken(): ReturnType<
  typeof useMutation<{ token: ApiTokenDTO; plaintext: string }, Error, { nome: string; escopo?: string }>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) =>
      api<{ token: ApiTokenDTO; plaintext: string }>('/api/tokens', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  });
}

export function useRevokeToken(): ReturnType<typeof useMutation<void, Error, number>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api(`/api/tokens/${id}`, { method: 'DELETE' }) as Promise<void>,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tokens'] }),
  });
}
